/**
 * Suppresses the dev-overlay error for
 *   "ResizeObserver loop completed with undelivered notifications."
 *   "ResizeObserver loop limit exceeded"
 *
 * These warnings are emitted by Radix popovers / virtualised lists / any
 * component that resizes within a paint cycle. They are documented as
 * non-actionable by the WICG (see github.com/WICG/resize-observer/issues/38)
 * and the React app keeps running normally. We only stop the webpack
 * `react-refresh-overlay` from intercepting them so the big red dev modal
 * doesn't open every time a popover closes after a bulk reassign.
 *
 * No-op in production builds — the overlay only mounts in dev, so the global
 * `error` / `unhandledrejection` listeners stay un-installed in prod.
 */
export function suppressResizeObserverError(): void {
  if (typeof window === 'undefined') return;
  // Dev-only: never silently swallow errors in production builds.
  if (process.env.NODE_ENV === 'production') return;

  const isResizeObserverMessage = (msg: unknown): msg is string =>
    typeof msg === 'string' && msg.includes('ResizeObserver loop');

  // Runtime type guard for the `unhandledrejection` reason — preferred over a
  // blind `as` cast so unexpected shapes (e.g. a thrown number) are handled
  // safely instead of returning a fake `.message`.
  const reasonMessage = (reason: unknown): string | undefined => {
    if (typeof reason === 'string') return reason;
    if (reason && typeof reason === 'object' && 'message' in reason) {
      const m = (reason as { message?: unknown }).message;
      return typeof m === 'string' ? m : undefined;
    }
    return undefined;
  };

  window.addEventListener(
    'error',
    e => {
      if (isResizeObserverMessage(e.message)) {
        // Dev-only breadcrumb so engineers can see the suppressor firing
        // (e.g. when investigating a popover layout glitch).
        console.debug('[suppressResizeObserverError]', e.message);
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    },
    true,
  );

  window.addEventListener(
    'unhandledrejection',
    e => {
      const msg = reasonMessage(e.reason);
      if (msg && isResizeObserverMessage(msg)) {
        console.debug('[suppressResizeObserverError]', msg);
        e.stopImmediatePropagation();
        e.preventDefault();
      }
    },
    true,
  );
}
